<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  AVATAR_STYLES,
  createAvatarChoices,
  avatarToUpload,
} from './helpers/avatarLibrary';

const props = defineProps({
  defaultStyle: { type: String, default: 'initial-face' },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['select']);
const { t } = useI18n();
const dialog = ref(null);
const galleryOpen = ref(false);
const styleId = ref('initial-face');
const page = ref(0);
const choices = ref([]);
const selected = ref(null);
const loading = ref(false);
const applying = ref(false);
const error = ref('');
const busy = computed(() => loading.value || applying.value);
const styleLabels = computed(() => ({
  lorelei: t('AVATAR_LIBRARY.STYLES.PORTRAITS'),
  notionists: t('AVATAR_LIBRARY.STYLES.ILLUSTRATIONS'),
  'initial-face': 'Initial Face',
  'fun-emoji': 'Fun Emoji',
  'voxel-art': 'Voxel Art',
  critters: t('AVATAR_LIBRARY.STYLES.ANIMALS'),
  shapes: t('AVATAR_LIBRARY.STYLES.SHAPES'),
}));
const loadChoices = async () => {
  loading.value = true;
  error.value = '';
  selected.value = null;
  choices.value = [];
  try {
    choices.value = await createAvatarChoices(styleId.value, page.value);
  } catch {
    error.value = t('AVATAR_LIBRARY.LOAD_ERROR');
  } finally {
    loading.value = false;
  }
};
const open = async () => {
  if (busy.value || props.disabled) return;
  styleId.value = AVATAR_STYLES.includes(props.defaultStyle)
    ? props.defaultStyle
    : 'initial-face';
  page.value = 0;
  galleryOpen.value = true;
  dialog.value.open();
  await loadChoices();
};
const changeStyle = async value => {
  if (busy.value) return;
  styleId.value = value;
  page.value = 0;
  await loadChoices();
};
const moreChoices = async () => {
  if (busy.value) return;
  page.value += 1;
  await loadChoices();
};
const apply = async () => {
  if (!selected.value || busy.value || props.disabled) return;
  applying.value = true;
  error.value = '';
  try {
    const upload = await avatarToUpload(selected.value);
    if (!galleryOpen.value) return;
    emit('select', upload);
    dialog.value.close();
  } catch {
    error.value = t('AVATAR_LIBRARY.APPLY_ERROR');
  } finally {
    applying.value = false;
  }
};
</script>

<template>
  <Button
    :label="t('AVATAR_LIBRARY.OPEN')"
    icon="i-lucide-smile"
    color="slate"
    variant="faded"
    size="sm"
    :disabled="disabled || busy"
    @click="open"
  />
  <Dialog
    ref="dialog"
    :title="t('AVATAR_LIBRARY.TITLE')"
    :description="t('AVATAR_LIBRARY.DESCRIPTION')"
    :confirm-button-label="t('AVATAR_LIBRARY.USE')"
    :disable-confirm-button="!selected || busy || disabled"
    :is-loading="applying"
    :close-on-click-outside="!applying"
    width="xl"
    sticky-header-footer
    overflow-y-auto
    @confirm="apply"
    @close="galleryOpen = false"
  >
    <div class="space-y-4">
      <div
        class="flex flex-wrap gap-1"
        :aria-label="t('AVATAR_LIBRARY.CATEGORIES')"
      >
        <Button
          v-for="style in AVATAR_STYLES"
          :key="style"
          :label="styleLabels[style]"
          color="slate"
          :variant="styleId === style ? 'faded' : 'ghost'"
          size="sm"
          :aria-pressed="styleId === style"
          :disabled="busy"
          @click="changeStyle(style)"
        />
      </div>
      <p v-if="error" role="alert" class="text-sm text-n-ruby-11">
        {{ error }}
      </p>
      <p
        v-if="loading"
        role="status"
        class="py-8 text-center text-sm text-n-slate-11"
      >
        {{ t('AVATAR_LIBRARY.LOADING') }}
      </p>
      <div v-else class="grid grid-cols-4 gap-2 sm:grid-cols-6">
        <button
          v-for="(avatar, index) in choices"
          :key="avatar.id"
          type="button"
          class="relative aspect-square overflow-hidden rounded-xl border-2 p-1 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-50"
          :class="
            selected?.id === avatar.id
              ? 'border-n-brand bg-n-brand/10'
              : 'border-n-weak bg-n-solid-2 hover:border-n-strong'
          "
          :aria-label="
            t('AVATAR_LIBRARY.OPTION', { number: page * 24 + index + 1 })
          "
          :aria-pressed="selected?.id === avatar.id"
          :disabled="busy"
          @click="selected = avatar"
        >
          <img
            :src="avatar.url"
            alt=""
            class="size-full rounded-lg object-contain"
          />
          <span
            v-if="selected?.id === avatar.id"
            class="absolute bottom-1 right-1 grid size-5 place-items-center rounded-full bg-n-brand text-white"
          >
            <span class="i-lucide-check size-3" />
          </span>
        </button>
      </div>
      <p
        v-if="styleId === 'fun-emoji'"
        class="flex flex-wrap gap-1 text-xs text-n-slate-11"
      >
        <a
          href="https://www.figma.com/community/file/968125295144990435"
          target="_blank"
          rel="noopener noreferrer"
          class="underline"
        >
          {{ t('AVATAR_LIBRARY.FUN_EMOJI_SOURCE') }}
        </a>
        <a
          href="https://creativecommons.org/licenses/by/4.0/"
          target="_blank"
          rel="noopener noreferrer"
          class="underline"
        >
          {{ t('AVATAR_LIBRARY.FUN_EMOJI_CREDITS') }}
        </a>
      </p>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <a
          href="https://www.dicebear.com/licenses/"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs text-n-slate-11 underline underline-offset-2"
        >
          {{ t('AVATAR_LIBRARY.CREDITS') }}
        </a>
        <Button
          :label="t('AVATAR_LIBRARY.MORE')"
          icon="i-lucide-refresh-cw"
          color="slate"
          variant="ghost"
          size="sm"
          :disabled="busy"
          @click="moreChoices"
        />
      </div>
    </div>
  </Dialog>
</template>
